<script setup lang="ts">
    import { ListboxOptions, ListboxOption } from '@headlessui/vue';
    import type { FormSelectOptionsValueBase } from '~/components/atm/FormSelectOptionsValue.vue';

    export type FormSelectOption = {
        disabled?: Ref<boolean>;
        goToSectionId?: string;
        label?: string;
        value: string;
    };

    export interface FormSelectOptionsProps extends FormSelectOptionsValueBase {
        open: boolean;
        teleport?: boolean;
        hideOverlay?: boolean;
        options: FormSelectOption[];
        optionsWidth?: number;
        optionsTop?: number;
        optionsLeft?: number;
    }

    defineProps<FormSelectOptionsProps>();

    const isMobile = ref(false);
    useMediaQuery([
        {
            query: defaultQueries.max.mobile,
            ref: isMobile,
        },
    ]);
</script>

<template>
    <transition
        enter-from-class="opacity-0"
        enter-active-class="duration-300 ease-in"
        enter-to-class="opacity-100"
        leave-from-class="opacity-100"
        leave-active-class="duration-300 ease-in"
        leave-to-class="opacity-0">
        <lazy-atm-overlay
            v-if="open && (!hideOverlay || isMobile)"
            no-blur
            class="z-3xl" />
    </transition>
    <transition
        enter-from-class="opacity-0"
        enter-active-class="duration-300 ease-in"
        enter-to-class="opacity-100"
        leave-from-class="opacity-100"
        leave-active-class="duration-300 ease-in"
        leave-to-class="opacity-0">
        <listbox-options
            :style="{
                '--width': `${optionsWidth}px`,
                '--top': `${optionsTop}px`,
                '--left': `${optionsLeft}px`,
            }"
            :class="[
                'atm-form-select-options z-3xl max-h-[60vh] w-full overflow-auto rounded-t-md bg-woom-white ring-1 ring-woom-black ring-opacity-5 focus:outline-none max-md:fixed max-md:bottom-0 max-md:left-0 md:absolute md:rounded-b-md',
                { 'md:left-[--left] md:top-[--top] md:w-[--width]': teleport },
            ]">
            <template
                v-for="(option, index) in options"
                :key="option.value + index">
                <listbox-option
                    v-slot="{ active, selected }"
                    :value="option.value"
                    as="template">
                    <li
                        :class="[
                            active ? 'bg-woom-grey text-woom-red active:bg-woom-grey' : 'text-woom-black',
                            'relative cursor-pointer select-none py-4 pl-3 hover:bg-woom-grey md:py-2',
                            {
                                'opacity-40': option.disabled?.value,
                            },
                        ]">
                        <atm-form-select-options-value
                            :option="option"
                            :option-multi-line="optionMultiLine"
                            :selected="selected"
                            :translate="translate"
                            :prefixComponent="prefixComponent"
                            :postfixComponent="postfixComponent" />
                    </li>
                </listbox-option>
            </template>
        </listbox-options>
    </transition>
</template>
